import { Layer, Marker, Source } from 'react-map-gl/mapbox';
import { Box } from '@mui/material';
import { RegridResponse } from '../../../../models/regrid.model.tsx';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { useCallback, useMemo } from 'react';
import {
  findCenterLatLon,
  calcAngle,
  getMiddleBetweenCoordinates,
  getSetbackCoordinates,
} from '../../utils/coords-math.ts';
import { translateZoningInfoNumberToString } from '../../../../utils/zoning-info.ts';
import { LotSideIndexes } from '../../types/position.ts';

interface SetbackSourceProps {
  lotSideIndexes: LotSideIndexes;
  setLotSideIndexes: React.Dispatch<React.SetStateAction<LotSideIndexes>>;
  validVertices: [number, number][];
  mapType: string;
  regridData?: RegridResponse;
  zoom?: number;
}

export const SetbackSource = ({
  lotSideIndexes,
  setLotSideIndexes,
  validVertices,
  mapType,
  regridData,
  zoom,
}: SetbackSourceProps) => {
  const setbackCoordinates = useMemo(
    () => getSetbackCoordinates(validVertices || [], lotSideIndexes, regridData),
    [validVertices, lotSideIndexes, regridData]
  );

  const setbackMiddlePoints = useMemo(
    () =>
      setbackCoordinates.length
        ? setbackCoordinates.map((coor, index) => {
            const secondIndex =
              index === setbackCoordinates.length - 1 ? 0 : index + 1;
            return getMiddleBetweenCoordinates(
              coor,
              setbackCoordinates[secondIndex]
            )!;
          })
        : [],
    [setbackCoordinates]
  );

  const setSideOfProperty = useCallback(
    (key: number) => {
      if (
        lotSideIndexes.frontSideIndex === -1 &&
        lotSideIndexes.backSideIndex === -1
      ) {
        setLotSideIndexes((prevIndexes) => {
          return {
            ...prevIndexes,
            frontSideIndex: key,
          };
        });
      } else if (
        lotSideIndexes.frontSideIndex !== -1 &&
        lotSideIndexes.backSideIndex === -1
      ) {
        setLotSideIndexes((prevIndexes) => {
          return {
            ...prevIndexes,
            backSideIndex: key,
          };
        });
      }
    },
    [
      lotSideIndexes.backSideIndex,
      lotSideIndexes.frontSideIndex,
      setLotSideIndexes,
    ]
  );

  const tooltipText = (key: number) => {
    const frontSetback =
      regridData?.zoningInformation.minimumFrontSetbackFeet || -1;
    const rearSetback =
      regridData?.zoningInformation.minimumRearSetbackFeet || -1;
    const sideSetback =
      regridData?.zoningInformation.minimumSideSetbackFeet || -1;
    // if we haven't selected any sides
    if (
      lotSideIndexes.frontSideIndex === -1 &&
      lotSideIndexes.backSideIndex === -1
    ) {
      return 'Select as front';
      // if we have selected front side but not the back
    } else if (
      lotSideIndexes.frontSideIndex !== -1 &&
      lotSideIndexes.backSideIndex === -1
    ) {
      if (lotSideIndexes.frontSideIndex !== key) {
        return 'Select as back';
      } else {
        return `Front - ${translateZoningInfoNumberToString(frontSetback, ' ft')}`;
      }
      // if we have front and back selected
    } else {
      if (lotSideIndexes.frontSideIndex === key) {
        return `Front - ${translateZoningInfoNumberToString(frontSetback, ' ft')}`;
      } else if (lotSideIndexes.backSideIndex === key) {
        return `Back - ${translateZoningInfoNumberToString(rearSetback, ' ft')}`;
      } else {
        return `Side - ${translateZoningInfoNumberToString(sideSetback, ' ft')}`;
      }
    }
  };

  return (
    <>
      {lotSideIndexes.frontSideIndex !== -1 &&
      lotSideIndexes.backSideIndex !== -1 ? null : (
        <Marker
          longitude={findCenterLatLon(setbackMiddlePoints).longitude || 0}
          latitude={findCenterLatLon(setbackMiddlePoints).latitude || 0}
          anchor="bottom"
        >
          <Box
            className="button-tooltip"
            data-satellite-map={mapType === 'satellite'}
            style={{
              transform: zoom ? `scale(${Math.min(1, Math.max(0.5, Math.pow(2, zoom - 18)))})` : 'scale(1)',
              transformOrigin: 'center center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {lotSideIndexes.frontSideIndex !== -1
              ? 'SELECT AS BACK'
              : 'SELECT AS FRONT'}
          </Box>
        </Marker>
      )}
      {setbackMiddlePoints.length &&
        setbackMiddlePoints.slice(0, setbackMiddlePoints.length - 1).map((middle, key) => {
          const secondIndex = key === setbackMiddlePoints.length - 1 ? 0 : key + 1;
          const tooltipAngle = calcAngle(
            setbackCoordinates[key][0],
            setbackCoordinates[key][1],
            setbackCoordinates[secondIndex][0],
            setbackCoordinates[secondIndex][1]
          );
          const showingSetbacks =
            lotSideIndexes.frontSideIndex === key ||
            lotSideIndexes.backSideIndex === key ||
            (lotSideIndexes.frontSideIndex !== -1 &&
              lotSideIndexes.backSideIndex !== -1);
          return (
            <Marker
              key={key}
              longitude={middle?.longitude || 0}
              latitude={middle?.latitude || 0}
              style={{
                pointerEvents: showingSetbacks ? 'none' : 'initial',
                zIndex: showingSetbacks ? 1 : 'initial',
              }}
              anchor="bottom"
            >
              {showingSetbacks ? (
                <Box
                  className="button-tooltip"
                  data-showing-setback={showingSetbacks}
                  data-key={key}
                  data-satellite-map={mapType === 'satellite'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    rotate: `${tooltipAngle > 0 ? tooltipAngle - 90 : tooltipAngle + 90}deg`,
                    transform: zoom ? `scale(${Math.min(1, Math.max(0.5, Math.pow(2, zoom - 18)))})` : 'scale(1)',
                    transformOrigin: 'center center'
                  }}
                  onClick={() => {
                    setSideOfProperty(key);
                  }}
                >
                  {tooltipText(key)}
                </Box>
              ) : (
                <Box
                  style={{
                    transform: 'translateY(20px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => {
                    setSideOfProperty(key);
                  }}
                >
                  <TouchAppIcon
                    sx={{
                      rotate: '-40deg',
                      color: mapType === 'satellite' ? 'white' : '#164570',
                      width: '32px',
                      height: '32px'
                    }}
                  />
                </Box>
              )}
            </Marker>
          );
        })}
      {/* Setback polygon */}
      {lotSideIndexes.frontSideIndex !== -1 &&
        lotSideIndexes.backSideIndex !== -1 && (
          <Source
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'Polygon',
                    coordinates: [setbackCoordinates],
                  },
                },
              ],
            }}
          >
            <Layer
              {...{
                id: 'data-setback',
                type: 'line',
                paint: {
                  'line-opacity': 1,
                  'line-width': 3,
                  'line-color': '#8DCC8C',
                },
              }}
            />
          </Source>
        )}
    </>
  );
};
